import React, { useState, useEffect } from 'react';
import { Utils, authJsonHeaders, handleSessionExpired, BILLING_CURRENCY_CODES, findOptionByValue } from '../../utils';
import { toast } from 'react-toastify';

const AdminUserCustomPricing = ({ userId, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [editRows, setEditRows] = useState([]);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [currency, setCurrency] = useState(BILLING_CURRENCY_CODES[0]?.value ?? 1);

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(`${Utils.baseurl}admin/users/billing-pricing-get`, {
                    method: 'POST',
                    headers: authJsonHeaders(Utils.storageKeys.adminToken),
                    body: JSON.stringify({ userId }),
                });
                const result = await res.json();
                if (cancelled) return;
                if (handleSessionExpired(res, result)) return;
                if (result.status && result.data) {
                    const d = result.data;
                    setHasSubscription(d.has_subscription);
                    setBillingCycle(d.billing_cycle || 'monthly');
                    setCurrency(d.currency ?? BILLING_CURRENCY_CODES[0]?.value ?? 1);
                    const rows = (d.custom_pricing || d.default_pricing || []).map((r) => ({ ...r }));
                    setEditRows(rows);
                }
            } catch (e) {
                console.error(e);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [userId]);

    const updateRow = (type, price) => {
        setEditRows((prev) => prev.map((row) => (row.type === type ? { ...row, price } : row)));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${Utils.baseurl}admin/users/billing-pricing-save`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({
                    userId,
                    currency,
                    billing_cycle: billingCycle,
                    custom_pricing: editRows.map((r) => ({ type: r.type, price: r.price })),
                }),
            });
            const result = await res.json();
            if (handleSessionExpired(res, result)) return;
            if (result.status) {
                toast.success(result.message || 'Billing settings saved');
                onSaved?.(result.data);
            } else {
                toast.error(result.message || 'Save failed');
            }
        } catch (e) {
            console.error(e);
            toast.error('Save failed');
        }
        setSaving(false);
    };

    if (loading) return <p style={{ fontSize: '13px', color: '#6b7280' }}>Loading pricing...</p>;
    if (!hasSubscription) return <p style={{ fontSize: '13px', color: '#6b7280' }}>No subscription — user has not selected a plan yet.</p>;

    return (
        <div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {['monthly', 'annual'].map((cycle) => (
                    <button
                        key={cycle}
                        type="button"
                        onClick={() => setBillingCycle(cycle)}
                        style={{
                            padding: '8px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px',
                            background: billingCycle === cycle ? '#4f46e5' : '#f3f4f6',
                            color: billingCycle === cycle ? '#fff' : '#374151',
                        }}
                    >
                        {cycle === 'monthly' ? 'Monthly' : 'Annual'}
                    </button>
                ))}
                <select
                    value={currency}
                    onChange={(e) => setCurrency(Number(e.target.value))}
                    style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }}
                >
                    {BILLING_CURRENCY_CODES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                </select>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                    <tr style={{ background: '#f3f4f6' }}>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Metric</th>
                        <th style={{ padding: '8px', textAlign: 'right', width: '120px' }}>Rate</th>
                    </tr>
                </thead>
                <tbody>
                    {editRows.map((row) => (
                        <tr key={row.type} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px' }}>{row.type_label}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={row.price}
                                    onChange={(e) => updateRow(row.type, e.target.value)}
                                    style={{ width: '100%', padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: '6px', textAlign: 'right' }}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p style={{ margin: '8px 0 12px', fontSize: '12px', color: '#dc2626' }}>
                Currency #{currency} ({findOptionByValue(BILLING_CURRENCY_CODES, currency)?.label}). Overrides global plan_pricing for this user.
            </p>
            <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{ padding: '10px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
            >
                {saving ? 'Saving...' : 'Save custom pricing'}
            </button>
        </div>
    );
};

export default AdminUserCustomPricing;
